import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../api';
import NotificationCard from '../Cards/NotificationCard';
import NotificationInfoPanel from '../Cards/NotificationInfoPanel';

const NotificationsModal = ({ isOpen, onClose, userId }) => {
   const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'wait', 'resolved'
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const intervalRef = useRef(null);
  const notificationsRef = useRef([]);
  console.log("userId для уведомлений: ", userId);

  // Обновляем ref при изменении notifications
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Сброс флага initial load при изменении фильтра или userId
  useEffect(() => {
    setIsInitialLoad(true);
  }, [statusFilter, userId]);

  const loadNotifications = async () => {
    try {
      // Устанавливаем loading только при первоначальной загрузке или смене фильтра
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      let url = `/approve/${userId}/list`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      // Если statusFilter === 'all', не передаем параметр status

      const response = await apiRequest(url, 'GET');
      console.log("response: ", response);

      const newNotifications = response.data.items || [];

      // Сравниваем данные по id и created_at
      const areNotificationsEqual = notificationsRef.current.length === newNotifications.length &&
        notificationsRef.current.every((notif, index) => notif.id === newNotifications[index].id &&
          notif.created_at === newNotifications[index].created_at);

      if (!areNotificationsEqual) {
        setNotifications(newNotifications);
      }

      // Сбрасываем флаг после первой загрузки
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (err) {
      console.error('Ошибка при загрузке уведомлений:', err);
      setError('Не удалось загрузить уведомления');
    } finally {
      // Убираем loading только если это была первоначальная загрузка
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Первоначальная загрузка при открытии модального окна
  useEffect(() => {
    if (isOpen && userId) {
      setIsInitialLoad(true);
      loadNotifications();
    }
  }, [isOpen, userId]);

  // Смена фильтра - сбрасываем флаг и загружаем заново
  useEffect(() => {
    if (isOpen && userId) {
      setIsInitialLoad(true);
      loadNotifications();
    }
  }, [statusFilter]);

  // Перезапрос уведомлений каждые 10 секунд (без loading)
  useEffect(() => {
    if (isOpen && userId && !isInitialLoad) {
      intervalRef.current = setInterval(() => {
        loadNotifications();
      }, 10000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isOpen, userId, isInitialLoad]);

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
  };

  const closeInfoPanel = () => {
    setSelectedNotification(null);
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const handleHeaderClick = (e) => {
    closeInfoPanel();
  };

  const handleBodyClick = (e) => {
    const panel = e.currentTarget.querySelector('.notification-details-panel.open');
    if (panel && !panel.contains(e.target)) {
      closeInfoPanel();
    }
  };

  const handleDetailsPanelClick = (e) => {
    closeInfoPanel();
  };

  if (!isOpen) return null;
  console.log("notifications: ", notifications);
  return (
    <div className={isMobile ? "modal-overlay-mobile" : "modal-overlay"} onClick={onClose}>
      <div
        className="modal fullscreen bottom-right open"
        onClick={handleModalClick}
      >
        <div className="modal-header" onClick={handleHeaderClick}>
          {isMobile && (
              <button className="modal-close-btn" onClick={onClose} aria-label="Закрыть">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          <h3>Уведомления</h3>
        </div>
        <div className="modal-body" onClick={handleBodyClick}>
          {loading ? (
            <div className="loading">Загрузка уведомлений...</div>
          ) : (
            <div className="notifications-container">
              <div className="notifications-filters">
                <button
                    className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                    onClick={(e) => {
                      closeInfoPanel();
                      setStatusFilter('all');
                    }}
                >
                  Все
                </button>
                <button
                    className={`filter-btn ${statusFilter === 'wait' ? 'active' : ''}`}
                    onClick={(e) => {
                      closeInfoPanel();
                      setStatusFilter('wait');
                    }}
                >
                  Ожидают
                </button>
                <button
                    className={`filter-btn ${statusFilter === 'success' ? 'active' : ''}`}
                    onClick={(e) => {
                      closeInfoPanel();
                      setStatusFilter('success');
                    }}
                >
                  Одобрено
                </button>
                <button
                    className={`filter-btn ${statusFilter === 'reject' ? 'active' : ''}`}
                    onClick={(e) => {
                      closeInfoPanel();
                      setStatusFilter('reject');
                    }}
                >
                  Отклонено
                </button>
              </div>

              {notifications.length === 0 ? (
                  <div className="no-notifications">Нет уведомлений</div>
              ) : error ? (
                  <div className="error">{error}</div>
              ) : (
                  <div className="notifications-list">
                    {notifications.map((notification) => (
                    <NotificationCard
                      key={notification.id}
                      notification={notification}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNotificationClick(notification);
                      }}
                    />
                  ))}
                </div>
              )}

              <div
                className={`notification-details-panel ${selectedNotification ? 'open' : ''}`}
                onClick={handleDetailsPanelClick}
              >
                {selectedNotification && (
                  <NotificationInfoPanel
                    notification={selectedNotification}
                    onClose={closeInfoPanel}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;
